import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const DocumentManager = ({ documents, onRefresh }) => {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentType, setDocumentType] = useState('compliance');
  const [searchTerm, setSearchTerm] = useState('');

  const documentTypes = [
    { value: 'compliance', label: 'Compliance Document', icon: 'Shield' },
    { value: 'certificate', label: 'Certificate', icon: 'Award' },
    { value: 'contract', label: 'Contract', icon: 'FileText' },
    { value: 'id_proof', label: 'ID Proof', icon: 'CreditCard' },
    { value: 'other', label: 'Other', icon: 'File' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'text-success bg-success/10 border-success/20';
      case 'pending': return 'text-warning bg-warning/10 border-warning/20';
      case 'rejected': return 'text-error bg-error/10 border-error/20';
      case 'expired': return 'text-muted-foreground bg-muted/10 border-muted/20';
      default: return 'text-muted-foreground bg-muted/10 border-muted/20';
    }
  };

  const getDocumentIcon = (type) => {
    const docType = documentTypes?.find(dt => dt?.value === type);
    return docType?.icon || 'File';
  };

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !user) return;

    setUploading(true);
    try {
      // Insert document record
      const { data, error } = await supabase
        ?.from('documents')
        ?.insert({
          owner_id: user?.id,
          owner_type: 'partner',
          document_type: documentType,
          file_name: selectedFile?.name,
          file_size: selectedFile?.size,
          status: 'pending'
        })
        ?.select()
        ?.single();

      if (error) throw error;

      // Reset form
      setSelectedFile(null);
      setDocumentType('compliance');
      
      // Refresh documents list
      if (onRefresh) {
        await onRefresh();
      }

      alert('Document uploaded successfully!');
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const filteredDocuments = documents?.filter(doc =>
    doc?.file_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    doc?.document_type?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  ) || [];

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <div className="glass-container rounded-xl border border-border/50 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
            <Icon name="Upload" size={24} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Upload Document</h2>
            <p className="text-sm text-muted-foreground">Add new documents to your application</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Document Type
            </label>
            <select
              value={documentType}
              onChange={(e) => setDocumentType(e?.target?.value)}
              className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {documentTypes?.map((type) => (
                <option key={type?.value} value={type?.value}>
                  {type?.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Select File
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="file"
                onChange={handleFileSelect}
                className="flex-1 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm file:mr-4 file:py-1 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
              />
              <Button
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                loading={uploading}
                iconName="Upload"
              >
                Upload
              </Button>
            </div>
            {selectedFile && (
              <p className="text-sm text-muted-foreground mt-2">
                Selected: {selectedFile?.name} ({(selectedFile?.size / 1024)?.toFixed(2)} KB)
              </p>
            )}
          </div>
        </div>
      </div>
      {/* Documents List */}
      <div className="glass-container rounded-xl border border-border/50 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-lg bg-secondary/10 border border-secondary/20 flex items-center justify-center">
              <Icon name="FolderOpen" size={24} className="text-secondary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">My Documents</h2>
              <p className="text-sm text-muted-foreground">{documents?.length || 0} documents uploaded</p>
            </div>
          </div>
          <Input
            type="text"
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-64"
          />
        </div>

        {filteredDocuments?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="FileX" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {searchTerm ? 'No documents found matching your search' : 'No documents uploaded yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredDocuments?.map((doc) => (
              <div
                key={doc?.id}
                className="flex items-center justify-between p-4 rounded-lg border border-border/50 hover:bg-accent/5 transition-colors"
              >
                <div className="flex items-center space-x-4 flex-1">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center">
                    <Icon name={getDocumentIcon(doc?.document_type)} size={20} className="text-accent" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-foreground">{doc?.file_name}</h4>
                    <div className="flex items-center space-x-4 text-sm text-muted-foreground mt-1">
                      <span className="capitalize">{doc?.document_type?.replace('_', ' ')}</span>
                      <span>•</span>
                      <span>{doc?.file_size ? `${(doc?.file_size / 1024)?.toFixed(2)} KB` : 'Unknown size'}</span>
                      <span>•</span>
                      <span>{doc?.uploaded_at ? new Date(doc.uploaded_at)?.toLocaleDateString() : 'Unknown date'}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(doc?.status)}`}>
                    {doc?.status?.toUpperCase()}
                  </div>
                  <Button variant="ghost" size="icon" iconName="Download" />
                  <Button variant="ghost" size="icon" iconName="Eye" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentManager;